import { LogOut } from "lucide-react";
import type { ReactNode } from "react";
import { Avatar, AvatarFallback } from "./avatar";
import { Button } from "./button";
import logoMobile from "./assets/evaluans-logo.png";
import logoDesktop from "./assets/evaluans.png";

export interface NavbarProps {
  /** Title or logo element */
  title: string | ReactNode;
  /** User display info */
  userInfo?: {
    displayName: string;
    subtitle: string;
    initials: string;
  };
  /** Logout handler */
  onLogout?: () => void;
  /** Additional left-side content (e.g., hamburger menu, search) */
  leftContent?: ReactNode;
  /** Content displayed between title and user info (e.g., navigation menu) */
  centerContent?: ReactNode;
  /** Additional right-side content */
  rightContent?: ReactNode;
  /** Whether logout is in progress */
  isLoggingOut?: boolean;
}

export function Navbar({
  title,
  userInfo,
  onLogout,
  leftContent,
  centerContent,
  rightContent,
  isLoggingOut = false,
}: NavbarProps) {
  return (
    <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
      <div className="container mx-auto grid grid-cols-[1fr_auto_1fr] h-14 items-center px-4 w-screen">
        <div className="flex items-center gap-2">
          {leftContent}
          <img src={logoMobile} alt="Logo" className="h-8 w-auto sm:hidden" />
          <img src={logoDesktop} alt="Logo" className="h-8 w-auto hidden sm:block" />
        </div>

        <div className="flex items-center justify-center font-semibold">
          {typeof title === "string" ? <span>{title}</span> : title}
        </div>

        <div className="flex items-center justify-end gap-2">
          {centerContent && (
            <div className="hidden md:flex items-center">{centerContent}</div>
          )}

          {rightContent}

          {userInfo && (
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-foreground">
                  {userInfo.displayName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {userInfo.subtitle}
                </p>
              </div>
              <Avatar className="h-7 w-7 sm:h-8 sm:w-8 bg-primary">
                <AvatarFallback className="bg-primary text-xs text-primary-foreground">
                  {userInfo.initials}
                </AvatarFallback>
              </Avatar>
            </div>
          )}

          {onLogout && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onLogout}
              disabled={isLoggingOut}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
